import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Trans } from '@lingui/react';
import { GqlShareIssue, GqlSiPaymentMethodsCountResponse } from '../../../../graphql/base-types.graphql';
import { AlertConfirmDialog } from '../../../../components/simple-dialogs/AlertConfirmDialog';
import { useState } from 'react';
import { SipShareSubscriptionNoteListDocument } from '../../../common/components/documents/SipShareSubscriptionNoteList.graphql';
import { useSiSetPaymentMethodMutation } from './ShareIssueStepper.graphql';
import { FormValues, StepPaymentMethods } from '../../../common/components/StepPaymentMethods';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';

const hasPaymentKindSelected = (paymentMethodsCount: GqlSiPaymentMethodsCountResponse, input: FormValues) => {
  if (paymentMethodsCount.cash && !input.paymentKindCash) {
    return true;
  }
  if (paymentMethodsCount.offset && !input.paymentKindOffset) {
    return true;
  }
  if (paymentMethodsCount.nonCash && !input.paymentKindNonCash) {
    return true;
  }
  return false;
};

export const SiStepPaymentMethods = ({
  shareIssue: { id, companyId, paymentKindCash, paymentKindNonCash, paymentKindOffset, paymentMethodsCount },
}: {
  shareIssue: Pick<
    GqlShareIssue,
    'id' | 'companyId' | 'paymentKindCash' | 'paymentKindNonCash' | 'paymentKindOffset' | 'paymentMethodsCount'
  >;
}) => {
  const { stepNext } = useStepper();
  const [doSetPaymentMethods, { loading }] = useSiSetPaymentMethodMutation({
    refetchQueries: [ShareIssueDocument, SipShareSubscriptionNoteListDocument],
    awaitRefetchQueries: true,
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState<FormValues | null>(null);

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { paymentKindCash, paymentKindOffset, paymentKindNonCash },
  });

  const doSubmit = async (values: FormValues) => {
    await doSetPaymentMethods({ variables: { companyId, input: { id, ...values } } });
    return stepNext();
  };

  const onSubmit: SubmitHandler<FormValues> = async values => {
    if (hasPaymentKindSelected(paymentMethodsCount, values)) {
      setShowConfirmDialog(values);
    } else {
      await doSubmit(values);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Typography level="h4">
            <Trans id="Payment Methods" />
          </Typography>
          <Alert>
            <Trans id="Here you choose which payments methods that should be allowed in the share issue" />
          </Alert>

          <StepPaymentMethods control={control} />

          <Box>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
      {showConfirmDialog && (
        <AlertConfirmDialog
          onClose={() => setShowConfirmDialog(null)}
          title={<Trans id="Participants with invalid payment method" />}
          content={
            <Trans id="There are one or more participant with invalid payment method. Changing this setting will result in those participants being removed." />
          }
          onConfirm={async () => {
            await doSubmit(showConfirmDialog);
            setShowConfirmDialog(null);
          }}
        />
      )}
    </>
  );
};
